import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { Divider, FlexLayout, GridLayout } from '@Styled/utilities';
import { rgba } from 'polished';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { object, string } from 'yup';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import AppSettingsApi from '../Services/AppSettings.requester';
import { AppSetting } from '../Types/AppSetting.types';
import {
	MarketingSettingsPayload,
	updateMarketingSettings,
} from './marketing.requester';

/**
 * Marketing tracking IDs. A dedicated subtab rather than more fields on the
 * Overview form on purpose: that form PUTs its whole object, so every field it
 * knows must be threaded through defaultValues + schema or it gets silently
 * dropped (see SettingsForm.tsx). This form sends ONLY its five keys, so the
 * two forms cannot clobber each other's columns.
 *
 * Soft validation only — the backend caps length at 64; format placeholders
 * guide the eye but odd-looking vendor IDs must still save.
 */
const idField = () =>
	string()
		.trim()
		.max(64, 'Vendor IDs are at most 64 characters')
		.nullable(true)
		.transform((_, value) => (value === '' ? null : value));

const MARKETING_SCHEMA = object({
	gtm_container_id: idField(),
	ga4_measurement_id: idField(),
	meta_pixel_id: idField(),
	tiktok_pixel_id: idField(),
	clarity_project_id: idField(),
});

const MarketingForm: React.FC<{ data: AppSetting }> = ({ data }) => {
	const { displaySnackbar } = useSnackbar();

	const { control, handleSubmit, formState } = useForm<MarketingSettingsPayload>({
		resolver: yupResolver(MARKETING_SCHEMA),
		mode: 'all',
		defaultValues: {
			gtm_container_id: data.gtm_container_id ?? null,
			ga4_measurement_id: data.ga4_measurement_id ?? null,
			meta_pixel_id: data.meta_pixel_id ?? null,
			tiktok_pixel_id: data.tiktok_pixel_id ?? null,
			clarity_project_id: data.clarity_project_id ?? null,
		},
	});

	const onSave = async (form: MarketingSettingsPayload) => {
		try {
			await updateMarketingSettings(form);
			displaySnackbar('success', 'Marketing settings updated successfully');
		} catch (e) {
			const msg =
				e?.response?.data?.message || 'Unable to update marketing settings';
			displaySnackbar('error', msg);
		}
	};

	return (
		<GridLayout gridGap="2rem" gridTemplateColumns={{ sm: 'repeat(1,1fr)' }} pb="4rem">
			<Divider label="Tracking IDs" />
			<Note>
				An empty field means that tracker is disabled. The student portal
				injects Google Tag Manager only when a container ID is set; changes
				apply the next time the portal loads (other backend consumers may take
				up to 60s).
			</Note>
			<EdFormControl
				control={control}
				type="text"
				name="gtm_container_id"
				label="GTM container ID"
				placeholder="GTM-XXXXXXX"
				helperText="The master switch — with this empty, nothing is injected and the IDs below are inert."
			/>
			<EdFormControl
				control={control}
				type="text"
				name="ga4_measurement_id"
				label="GA4 measurement ID"
				placeholder="G-XXXXXXXXXX"
			/>
			<EdFormControl
				control={control}
				type="text"
				name="meta_pixel_id"
				label="Meta (Facebook) pixel ID"
				placeholder="Numeric pixel ID"
			/>
			<EdFormControl
				control={control}
				type="text"
				name="tiktok_pixel_id"
				label="TikTok pixel ID"
				placeholder="Alphanumeric pixel ID"
			/>
			<EdFormControl
				control={control}
				type="text"
				name="clarity_project_id"
				label="Microsoft Clarity project ID"
				placeholder="Alphanumeric project ID"
			/>
			<Divider label="For the GTM configurator" />
			<Note>
				The portal seeds the dataLayer, before the container loads, with these
				exact keys: <Code>ga4_measurement_id</Code>, <Code>meta_pixel_id</Code>,{' '}
				<Code>tiktok_pixel_id</Code>, <Code>clarity_project_id</Code>,{' '}
				<Code>brand</Code>, <Code>environment</Code>. Vendor tags in GTM should
				read their ID from the matching Data Layer Variable and fire only when
				it is non-empty. Use a History Change trigger for SPA page views.
			</Note>
			<FlexLayout display="flex" justifyContent="flex-end">
				<Button
					variant="contained"
					disabled={formState.isSubmitting}
					onClick={handleSubmit(onSave)}
				>
					Save
				</Button>
			</FlexLayout>
		</GridLayout>
	);
};

const MarketingTab = () => {
	const [settings, setSettings] = useState<AppSetting>();

	const getSettings = async () => {
		const {
			data: { data },
		} = await AppSettingsApi.getAppSettings();
		setSettings(data);
	};

	useEffect(() => {
		getSettings();
	}, []);

	return <Container>{settings && <MarketingForm data={settings} />}</Container>;
};

const Container = styled.div`
	min-height: 100vh;
	padding: 1rem 2rem;
	border: 1px solid ${rgba('#5AC0FC', 0.25)};
`;

const Note = styled.p`
	margin: 0;
	color: #6c757d;
	font-size: 0.875rem;
	max-width: 70ch;
`;

const Code = styled.code`
	background: ${rgba('#5AC0FC', 0.12)};
	border-radius: 3px;
	padding: 0.1rem 0.3rem;
`;

export default MarketingTab;
